import { ExpandFaces } from './expandFaces';
import { ExpandParams } from './expandParams';
import { ProductGroup } from '../../../../models/product-group';

import { Utils } from '../../../../../core/services/utils.service';

interface IExpandStrategy {
	execute: (products: Array<ProductGroup>, params: ExpandParams) => void;
	executeInGroup: (products: Array<ProductGroup>, params: ExpandParams) => void;
	executeOnPlanogram: (products: Array<ProductGroup>, params: ExpandParams) => void;
}

class ExpandDefault implements IExpandStrategy {

	constructor() {}

	execute(products: Array<ProductGroup>, params: ExpandParams) {
		let hasChanges: boolean = true;
		while(params.diff >= params.minWidth && hasChanges) {
			hasChanges = false;
			products.forEach(product => {
				const level = params.levelToProduct.get(product),
					facesQty = params.productsToLevel.get(level)
						.filter((pg: ProductGroup) => pg.product.id == product.product.id)
						.reduce((total: number, pg: ProductGroup) => total + pg.FacesQty, 0);
				if (product.ItemWidth <= params.diff && (!level.maxFacesQty || facesQty < level.maxFacesQty)) {
					product.FacesQty += 1;
					params.diff = Utils.round(params.diff - product.ItemWidth);
					hasChanges = true;
				}
			});
		}
	}

	executeInGroup(products: Array<ProductGroup>, params: ExpandParams) {
		this.execute(products, params);
	}

	executeOnPlanogram(products: Array<ProductGroup>, params: ExpandParams) {
		this.execute(products, params);
	}
}

class ExpandFromStart extends ExpandDefault {

	constructor() {
		super();
	}

	execute(products: Array<ProductGroup>, params: ExpandParams) {
		const sortedProducts = products.sort((a: ProductGroup, b: ProductGroup) => a.begin.x - b.begin.x);

		super.execute(sortedProducts, params);
	}
}

class ExpandByWidth extends ExpandDefault {

	constructor() {
		super();
	}

	execute(products: Array<ProductGroup>, params: ExpandParams) {
		const sortedProducts = products.sort((a: ProductGroup, b: ProductGroup) => a.FacesQty - b.FacesQty || a.ItemWidth - b.ItemWidth);

		super.execute(sortedProducts, params);
	}
}

class ExpandByPercent extends ExpandDefault {

	constructor() {
		super();
	}

	execute(products: Array<ProductGroup>, params: ExpandParams) {
		const sortedProducts = products.sort((a: ProductGroup, b: ProductGroup) => {
			const aProductPercent = params.cachedPercentDictionary[a.product.id] / params.percentToProduct.get(a.product),
				bProductPercent = params.cachedPercentDictionary[b.product.id] / params.percentToProduct.get(b.product);
			return aProductPercent - bProductPercent;
		});

		super.execute(sortedProducts, params);
	}

	executeOnPlanogram(products: Array<ProductGroup>, params: ExpandParams) {
		const groupedProducts = [];
		let prevLevel;
		products
			.sort((a: ProductGroup, b: ProductGroup) => {
				const aLevelPercent = params.levelToProduct.get(a).actualPercent / params.levelToProduct.get(a).calculatedPercent,
					bLevelPercent = params.levelToProduct.get(b).actualPercent / params.levelToProduct.get(b).calculatedPercent,
					aProductPercent = params.cachedPercentDictionary[a.product.id] / params.percentToProduct.get(a.product),
					bProductPercent = params.cachedPercentDictionary[b.product.id] / params.percentToProduct.get(b.product);
				return aLevelPercent - bLevelPercent || aProductPercent - bProductPercent;
			})
			.forEach((product: ProductGroup) => {
				if (params.levelToProduct.get(product) == prevLevel) {
					groupedProducts[groupedProducts.length - 1].push(product);
				} else {
					groupedProducts.push([product]);
				}
				prevLevel = params.levelToProduct.get(product);
			});
		const maxLength = Math.max(...groupedProducts.map(arr => arr.length));
		for (let j = 0; j < maxLength; j++) {
			for (let i = 0; i < groupedProducts.length; i++) {
				if (groupedProducts[i][j]) products.push(groupedProducts[i][j]);
			}
		}
		let hasChanges = true;
		while(params.diff >= params.minWidth && hasChanges) {
			hasChanges = false;
			products.forEach(product => {
				const level = params.levelToProduct.get(product),
					facesQty = params.productsToLevel.get(level)
						.filter((pg: ProductGroup) => pg.product.id == product.product.id)
						.reduce((total: number, pg: ProductGroup) => total + pg.FacesQty, 0),
					possibleMeters = Utils.round(level.actualMeters + product.ItemWidth);
				if (product.ItemWidth <= params.diff && possibleMeters <= level.plannedMeters && (!level.maxFacesQty || facesQty < level.maxFacesQty)) {
					product.FacesQty += 1;
					params.diff = Utils.round(params.diff - product.ItemWidth);
					level.actualMeters = possibleMeters;
					level.actualPercent = level.actualMeters / params.totalWidth * 100;
					hasChanges = true;
				}
			});
		}
	}
}

export class ExpandFactory {

	private static _strategies: Map<ExpandFaces, typeof ExpandDefault> = new Map<ExpandFaces, typeof ExpandDefault>([
		[ExpandFaces.Default, ExpandDefault],
		[ExpandFaces.FromStart, ExpandFromStart],
		[ExpandFaces.ByWidth, ExpandByWidth],
		[ExpandFaces.ByPercent, ExpandByPercent]
	]);

	constructor() {}

	static getStrategy(strategy: ExpandFaces) {
		return new (ExpandFactory._strategies.get(strategy || ExpandFaces.Default))();
	}
}